import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import axiosInstance from 'config/axiosInstance';
import { classifiedErrorMessages } from 'config/apiErrors';
import { buttonLike } from 'reusable/buttonLike';
import { useLatestRequest } from 'reusable/useLatestRequest';
import video_icon from '../assets/icons/video.svg';

/**
 * 測定の一覧の「動画」の欄（EV-193。PM の決定 2026-10-07）。
 *
 * サーバーの一覧は S3 へ問い合わせないため、未確定の測定の evidence を null で返し、
 * evidence_status（confirmed / unconfirmed / none）で区別する（BE-15・EV-186）。
 * - evidence がある: 従来どおり押すと再生する。
 * - 未確定（evidence_status が unconfirmed）: 「確認して再生」。押したときだけ詳細
 *   （GET /v1/alcohol_tests/<id>/）を呼び、evidence が返れば再生、null なら「動画を確認できませんでした」。
 * - EV-406。保管庫（Deep Archive）に移った動画は、取り出す（restore）まで再生できない。一覧は S3 へ問い合わせない
 *   ので、移る日数を過ぎた行を possibly_archived で返す。これも「確認して再生」にし、詳細が archived を返したら
 *   再生の代わりに「保管庫に移っています（取り出しに12時間ほどかかります）」と出す。一覧が HEAD して archived と
 *   分かった行（版の無い行）は、はじめからその文を出す（取り出しの後に押せば再生できる）。
 * - それ以外: 「動画なし」。
 *
 * EV-292（WR-AC1〜4）。欄は表によって狭い（「全ての報告」は 1440px で 87px）。日本語は文字ごとに折り返せるので、
 * 欄の文（「照合できない」「確認して再生」「動画なし」）は文字の間で折らない（break-keep。英語は語の境目でだけ
 * 折る）。欄は中身の最小の幅より狭くしない（min-w-min。広い欄の幅は変わらない）。「照合できない」は動画の名前の
 * 横で縮まないように、折り返しを許した欄の次の行を1行占める（flex-wrap・basis-full）。印と動画の名前は
 * 1つの行にまとめ、今までどおり横に並べる。失敗の文は長い文なので普通に折り返す。
 */
export interface EvidenceRow {
  id?: number;
  evidence?: string | null;
  /**
   * unverifiable: 照合の値を記録する前に確定した古い証跡（EV-089。URL は配られ、再生できる）。
   * unavailable: 保存先に接続できず有無や照合値を確かめられなかった（EV-364）。
   * archived: 保管庫に移っていて、取り出すまで再生できない（EV-406）。
   * possibly_archived: 一覧だけ。保管庫に移る日数を過ぎた行で、詳細で確かめる（EV-406）。
   */
  evidence_status?:
    | 'confirmed'
    | 'unverifiable'
    | 'unconfirmed'
    | 'none'
    | 'unavailable'
    | 'archived'
    | 'possibly_archived';
}

export interface EvidenceCheck {
  /** 確認の要求が送信中か。送信中は、どの行の「確認して再生」も押せない（RV-167）。 */
  busy: boolean;
  checkingId: number | null;
  /** tone: info は失敗ではない案内（保管庫に移った動画。EV-406）。無ければ失敗の文。 */
  notice: { id: number; message: string; tone?: 'info' } | null;
  /** element: 押したボタン。確認の後にフォーカスを戻す（EV-202）。 */
  check: (id: number, element?: HTMLElement | null) => void;
  /**
   * 確定済みの行の動画を直接開く（RV-170）。送信中の確認の応答を先に無効にしてから開くので、
   * 後から届いた確認の応答でモーダルの中身が替わらない。
   */
  play: (url: string) => void;
}

/**
 * 一覧1つにつき1回呼ぶ。確認の要求は一覧で1本だけにする（ADM-15）。
 * - RV-167。送信中は、どの行も押せない。行ごとに送信中を持つと、行 A の確認中に行 B を押したとき
 *   A の印が上書きされ、A への要求が重なった。表示できる動画は1つで、反映するのは最後の要求だけなので、
 *   1本の確認が終わる（成功・失敗・時間切れ）まで次を止める。止めるのは条件を変えた後も同じで、
 *   古い要求が終わるまで新しい要求を出さない（同じ行への要求が重ならない）。
 * - RV-166。一覧の行（rows）か一覧の条件（conditions。運転者・ページ・並び・期間・絞り込み）が
 *   変わったら、送信中の確認の応答を捨て、案内を消し、onReset（開いている動画を閉じる）を呼ぶ。
 *   新しい一覧が届く前（古い行が残っている間）に条件だけが変わった場合も捨てる。
 * - 画面を閉じた後の応答も捨てる（useLatestRequest）。
 * - 失敗（通信・403・500）は、動画が無い場合と別の文で出す。
 */
export function useEvidenceCheck(
  onPlay: (url: string) => void,
  rows: unknown,
  conditions: string,
  onReset?: () => void
): EvidenceCheck {
  const { t } = useTranslation('common');
  const beginRequest = useLatestRequest();
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [checkingId, setCheckingId] = useState<number | null>(null);
  const [notice, setNotice] = useState<EvidenceCheck['notice']>(null);
  const onResetRef = useRef(onReset);
  onResetRef.current = onReset;
  // EV-202。確認の後に、押したボタンへフォーカスを戻す。押している間はボタンが押せなくなり、
  // ブラウザはフォーカスを外すため、押せるようになった描画の直後に戻す（動画を開くときも、先に
  // ボタンへ戻しておくと、動画を閉じたときに useModalDialog がボタンへ戻す）。
  const returnFocusTo = useRef<HTMLElement | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  useLayoutEffect(() => {
    const element = returnFocusTo.current;
    returnFocusTo.current = null;
    // EV-206(3)。フォーカスが body（押せなくなったボタンから外れた）か押したボタンにあるときだけ戻す。
    // 確認の間に利用者が別の欄へ移したフォーカスは奪わない。
    const active = document.activeElement;
    const free = !active || active === document.body || active === element;
    if (focusRequest > 0 && element && element.isConnected && free) element.focus();
  }, [focusRequest]);

  // 描画の直後（次の操作より前）に確実に行うため useLayoutEffect にする（useEffect だと、新しい行を
  // 押した後に遅れて走りうる）。送信中の印（busy）は、その要求が終わるまで外さない。
  useLayoutEffect(() => {
    beginRequest();
    returnFocusTo.current = null;
    setCheckingId(null);
    setNotice(null);
    onResetRef.current?.();
  }, [rows, conditions, beginRequest]);

  const check = useCallback(
    async (id: number, element?: HTMLElement | null) => {
      if (inFlight.current) return;
      inFlight.current = true;
      returnFocusTo.current = element ?? null;
      setBusy(true);
      const isCurrent = beginRequest();
      setCheckingId(id);
      setNotice(null);
      try {
        const response = await axiosInstance.get(`/v1/alcohol_tests/${id}/`);
        if (!isCurrent()) return;
        const url = response.data?.evidence;
        if (url) {
          onPlay(url);
        } else if (response.data?.evidence_status === 'unavailable') {
          setNotice({ id, message: t('ui.evidence-unavailable') });
        } else if (response.data?.evidence_status === 'archived') {
          setNotice({ id, message: t('ui.evidence-archived'), tone: 'info' });
        } else {
          setNotice({ id, message: t('ui.evidence-not-found') });
        }
      } catch (error) {
        if (!isCurrent()) return;
        console.error('Failed to check the evidence', error);
        setNotice({
          id,
          message: classifiedErrorMessages(error, {
            network: t('ui.error-network'),
            forbidden: t('ui.error-forbidden'),
            server: t('ui.error-server'),
            fallback: t('ui.evidence-check-failed'),
          }).join(' '),
        });
      } finally {
        inFlight.current = false;
        setBusy(false);
        if (isCurrent()) {
          setCheckingId(null);
          setFocusRequest((n) => n + 1);
        }
      }
    },
    [beginRequest, onPlay, t]
  );

  // RV-170。確認中でも直接の再生は止めない（確定済みの動画は確認を待つ理由が無い）。代わりに、
  // 送信中の確認の応答を無効にしてから開く。送信中の印（busy）はその要求が終わるまで外さない（RV-167）。
  const play = useCallback(
    (url: string) => {
      beginRequest();
      setCheckingId(null);
      setNotice(null);
      onPlay(url);
    },
    [beginRequest, onPlay]
  );

  return { busy, checkingId, notice, check, play };
}

/**
 * 動画の名前に出してよい拡張子。サーバーが証跡として受け付ける形式（helpers.EVIDENCE_EXTENSIONS）と webm。
 */
const VIDEO_EXTENSIONS = new Set(['mp4', 'm4v', 'mov', 'qt', '3gp', '3g2', 'webm']);

/**
 * 動画の名前（例「e.mp4 (.mp4)」）。2026-10-24 ステージングの axe の確認（作業17）。
 *
 * 証跡の URL は署名付き URL で、クエリに証跡の版の ID・X-Amz-Credential・X-Amz-Security-Token・
 * X-Amz-Signature が入る。URL 全体の最後の「.」から拡張子を取り、URL の末尾5文字を名前にしていたため、
 * クエリに「.」がある行で署名の断片が文字として出て表からはみ出した（P2）。
 * 名前は URL のパス（pathname）の最後の部分からだけ作り、クエリ・断片は使わない。相対の URL も読み、
 * 読めない URL・拡張子の無い名前・証跡の形式でない拡張子は既定の名前（fallback）にする。
 */
function fileLabel(url: string, fallback: string) {
  let pathname: string;
  try {
    pathname = new URL(url, 'http://localhost/').pathname;
  } catch {
    return fallback;
  }
  let name = pathname.slice(pathname.lastIndexOf('/') + 1);
  try {
    name = decodeURIComponent(name);
  } catch {
    // 不正な %（例「%E0」）はそのままにする。
  }
  name = name.slice(name.lastIndexOf('/') + 1);
  const dot = name.lastIndexOf('.');
  const extension = dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
  if (!VIDEO_EXTENSIONS.has(extension)) return fallback;
  return `${name.slice(-5)} (.${extension})`;
}

interface EvidenceCellProps {
  submission: EvidenceRow;
  /** 再生は evidenceCheck.play を通す（送信中の確認を無効にしてから開く。RV-170）。 */
  evidenceCheck: EvidenceCheck;
  /** 欄の外枠の class（表ごとの幅・余白）。 */
  className: string;
  /** 行を区別する文（運転者名・測定日時など）。「確認して再生」の名前に含める（EV-203）。 */
  rowLabel?: string;
  iconClassName?: string;
}

const EvidenceCell: React.FC<EvidenceCellProps> = ({
  submission,
  evidenceCheck,
  className,
  rowLabel,
  iconClassName = '',
}) => {
  const { t } = useTranslation('common');
  const { evidence, evidence_status: status, id } = submission;
  const unverifiedId = useId();

  if (evidence) {
    // EV-089（2026-10-11 PM 判断）。照合の値の無い古い証跡は、再生できるまま「照合できない」と示す。
    const unverifiable = status === 'unverifiable';
    return (
      <div
        className={`${className} flex-wrap min-w-min cursor-pointer`}
        {...buttonLike(() => evidenceCheck.play(evidence), t('ui.play-video'))}
        aria-describedby={unverifiable ? `${unverifiedId}-label ${unverifiedId}-detail` : undefined}
      >
        {/* 印と動画の名前は1つの行にまとめる（欄の折り返しで印だけが上の行に分かれないように）。
            印は縮めず、百分率の max-width（Tailwind の preflight）を外す。外さないと印の最小の幅が 0 と数えられ、
            min-w-min でも欄が広がらずに動画の名前が枠からはみ出す。 */}
        <span className="flex items-center">
          <img src={video_icon} alt="" className={`shrink-0 max-w-none ${iconClassName}`} />
          {fileLabel(evidence, t('ui.video'))}
        </span>
        {unverifiable ? (
          <>
            <span
              id={`${unverifiedId}-label`}
              className="block basis-full text-center break-keep text-caption text-gray-500"
              title={t('ui.evidence-unverifiable-detail')}
            >
              {t('ui.evidence-unverifiable')}
            </span>
            <span id={`${unverifiedId}-detail`} className="sr-only">
              {t('ui.evidence-unverifiable-detail')}
            </span>
          </>
        ) : null}
      </div>
    );
  }

  if (
    (status === 'unconfirmed' ||
      status === 'unavailable' ||
      status === 'archived' ||
      status === 'possibly_archived') &&
    typeof id === 'number'
  ) {
    const checking = evidenceCheck.checkingId === id;
    const notice =
      evidenceCheck.notice?.id === id
        ? evidenceCheck.notice
        : status === 'unavailable'
        ? { message: t('ui.evidence-unavailable') }
        : status === 'archived'
        ? { message: t('ui.evidence-archived'), tone: 'info' as const }
        : null;
    return (
      <div className={`${className} flex-col min-w-min`}>
        <button
          type="button"
          className="break-keep text-primary underline disabled:opacity-50 disabled:no-underline"
          disabled={evidenceCheck.busy}
          aria-busy={checking}
          aria-label={rowLabel ? t('ui.check-and-play-row', { row: rowLabel }) : undefined}
          onClick={(event) => evidenceCheck.check(id, event.currentTarget)}
        >
          {t('ui.check-and-play')}
        </button>
        {notice ? (
          <span
            role="status"
            className={`text-caption ${notice.tone === 'info' ? 'text-gray-600' : 'text-red-500'}`}
          >
            {notice.message}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`min-w-min ${className}`}>
      <span className="break-keep text-gray-500">{t('ui.no-video')}</span>
    </div>
  );
};

export default EvidenceCell;
